import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminLaporanLemburPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherSuggestions, setTeacherSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);

  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Real data for overtime reports
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);
  // Always attempt to fetch data - the API will handle auth checks
  // This prevents issues where the user object might not be loaded yet

  const fetchReports = async (searchFilter = searchQuery, dateFilterParam = dateFilter, statusFilterParam = statusFilter) => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchFilter) params.append('search', searchFilter);
      if (dateFilterParam) params.append('date', dateFilterParam);
      if (statusFilterParam) params.append('status', statusFilterParam);
      params.append('page', currentPage.toString());
      params.append('per_page', itemsPerPage.toString());
      
      const queryString = params.toString();
      const url = `${apiBase}/api/admin/overtime-reports${queryString ? `?${queryString}` : ''}`;
      
      // Use the imported authFetch instead of window.authFetch
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch reports: ${res.status} ${errorText}`);
      }
      
      const response = await res.json();
      
      // Handle paginated response
      const sourceData = response.data || response || [];
      
      // Update pagination info if available
      if (response.current_page !== undefined) {
        setCurrentPage(response.current_page);
        setTotalPages(response.last_page || 1);
        setTotalItems(response.total || 0);
      }
      
      // Map backend data to UI format for overtime reports
      const mapped = sourceData.map(item => {

        return {
          id: item.id,
          // Store both formatted date for display and ISO date for form inputs
          tanggalPengajuan: item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '',
          // Keep original date in ISO format for date inputs
          tanggalPengajuanISO: item.created_at ? item.created_at.slice(0,10) : '',
          // Student name field is correct
          siswaMengajukan: item.student ? item.student.name : '',
          student: item.student,
          // Student has 'class' field, not 'kelas'
          kelas: item.student ? (item.student.class || '') : '',
          nis: item.student ? (item.student.nis || '') : '',
          // Overtime report specific fields
          tanggalLembur: item.overtime_date || '',
          tanggalLemburFormatted: item.overtime_date ? new Date(item.overtime_date).toLocaleDateString('id-ID', {
              day: 'numeric',
              month: 'short',
              year: 'numeric'
          }) : '',
          endOvertimeDate: item.end_overtime_date || '',
          jamMulai: item.start_time || '',
          jamSelesai: item.finish_time || '',
          overtimeHours: item.overtime_hours || '',
          alasan: item.purpose || '',
          jenisPekerjaan: item.type_or_work || '',
          guruBengkel: item.teacher_name || '',
          // Use request_status field instead of status
          status: item.request_status || item.status || 'menunggu',
          // Add images array
          images: item.images || [],
          decision_by_id: item.decision_by_id,
          decision_by_teacher_id: item.decision_by_teacher_id,
        };
      });

      setSuratIjinRequests(mapped);
    } catch (e) {
      setNotification({
        show: true,
        message: `Error fetching data: ${e.message}`,
        type: 'error'
      });
      
      // Auto-hide error notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
      
      setSuratIjinRequests([]);
    }
  };

  useEffect(() => {
    fetchReports();
    
    // Fetch admin users for decision maker info
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin_users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(data.data || data);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();

    // Fetch teachers for auto-suggest
    async function fetchTeachers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin/teachers/praktek`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setTeachers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setTeachers([]);
      }
    }
    fetchTeachers();
    
    // eslint-disable-next-line
  }, [searchQuery, dateFilter, statusFilter, currentPage, itemsPerPage]);

  const filteredRequests = suratIjinRequests.filter(request => {
    const q = searchQuery.toLowerCase();
    return (
      (request.siswaMengajukan && request.siswaMengajukan.toLowerCase().includes(q)) ||
      (request.kelas && request.kelas.toLowerCase().includes(q)) ||
      (request.nis && String(request.nis).toLowerCase().includes(q)) ||
      (request.guruBengkel && request.guruBengkel.toLowerCase().includes(q)) ||
      (request.alasan && request.alasan.toLowerCase().includes(q)) ||
      (request.jenisPekerjaan && request.jenisPekerjaan.toLowerCase().includes(q))
    );
  });

  // For server-side pagination, use the full filtered requests
  const currentPageItems = filteredRequests;
  const startIndex = ((currentPage - 1) * itemsPerPage) + 1;
  const endIndex = Math.min(startIndex + filteredRequests.length - 1, totalItems);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get admin name by decision_by_id
  const getDecisionMakerName = (permit) => {
    if (permit.decision_by_teacher_id) {
        const teacher = teachers.find(t => t.id === permit.decision_by_teacher_id);
        return teacher ? teacher.name : 'Guru';
    }
    if (permit.decision_by_id) {
        const admin = adminUsers.find(a => a.id === permit.decision_by_id);
        return admin ? admin.name : 'Admin';
    }
    return 'Belum diputuskan';
  };

  // Teacher auto-suggest handlers
  const handleTeacherInputChange = (value) => {
    setSelectedPermit({ ...selectedPermit, guruBengkel: value, teacher_id: '' });
    setSelectedTeacherId(null);
    
    // Filter teachers based on input
    if (value.trim()) {
      const filtered = teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(value.toLowerCase())
      );
      setTeacherSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setTeacherSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedPermit({ 
      ...selectedPermit, 
      guruBengkel: teacher.name,
      teacher_id: teacher.id 
    });
    setSelectedTeacherId(teacher.id);
    setShowSuggestions(false);
    setTeacherSuggestions([]);
  };

  const handleTeacherInputBlur = () => {
    // Delay hiding suggestions to allow for click
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student: student } });
    } else {
      setNotification({
        show: true,
        message: 'Data siswa tidak ditemukan atau tidak lengkap.',
        type: 'error',
      });
    }
  };

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilter) params.append('date', dateFilter);
      if (statusFilter) params.append('status', statusFilter);

      console.log('Making Excel export request to:', `/api/admin/overtime-reports/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const response = await authFetch(`/api/admin/overtime-reports/export-excel?${params.toString()}`, {
        method: 'PUT',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Lembur');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Laporan_Lembur_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename} (${data.data.length} records)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Gagal mengekspor data ke Excel';
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Sesi telah berakhir. Silakan login kembali.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Tidak memiliki akses. Silakan login kembali.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Tidak memiliki izin untuk mengekspor data.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Endpoint ekspor tidak ditemukan.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Terjadi kesalahan server. Silakan coba lagi.';
      } else if (error.message.includes('Invalid data format')) {
        errorMessage = 'Format data tidak valid dari server.';
      }
      
      setNotification({
        show: true,
        message: errorMessage,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Laporan Lembur</h1>
            <Subtitle>Kelola dan tinjau permintaan lembur</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <FiltersContainer>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama guru, siswa, atau mata pelajaran..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                <FilterGroup>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    placeholder="Filter tanggal"
                  />
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="menunggu">Menunggu</option>
                    <option value="disetujui">Disetujui</option>
                    <option value="ditolak">Ditolak</option>
                  </FilterSelect>
                  <ExportButton onClick={handleExcelExport}>
                    📊 Export Excel
                  </ExportButton>
                </FilterGroup>
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal Pengajuan</th>
                    <th>Siswa</th>
                    <th>Kelas</th>
                    <th>Tanggal Lembur</th>
                    <th>Jam Mulai</th>
                    <th>Jam Selesai</th>
                    <th>Alasan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{request.tanggalPengajuan}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.siswaMengajukan}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>

                        <td>{request.tanggalLemburFormatted}</td>
                        <td>{request.jamMulai}</td>
                        <td>{request.jamSelesai}</td>
                        <td>{request.alasan}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.status === 'pending' || request.status === 'menunggu' ? 'Menunggu' :
                              request.status === 'approved' || request.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada laporan
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalItems > 0 && (
              <PaginationContainer>
                <PaginationInfo>
              Menampilkan {startIndex}-{endIndex} dari {totalItems} data
            </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <PageNumber
                            key={i}
                            active={i === currentPage}
                            onClick={() => setCurrentPage(i)}
                          >
                            {i}
                          </PageNumber>
                        );
                      }
                      return pages;
                    })()} 
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Detail Laporan Lembur</h2>
                <StatusContainer>
                  <DetailStatus status={selectedPermit.status}>
                    {selectedPermit.status === 'pending' || selectedPermit.status === 'menunggu' ? 'Menunggu' :
                      selectedPermit.status === 'approved' || selectedPermit.status === 'disetujui' ? 'Disetujui' : 'Ditolak'}
                  </DetailStatus>
                  {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedPermit) || 'Herman Yosef'}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
            </DetailHeader>

            <DetailContent>
              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal Pengajuan</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalPengajuanISO || ''}
                      onChange={e => {
                        const newDate = e.target.value;
                        // Update both ISO and formatted date
                        setSelectedPermit({ 
                          ...selectedPermit, 
                          tanggalPengajuanISO: newDate,
                          tanggalPengajuan: newDate ? new Date(newDate).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          }) : ''
                        });
                      }}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Siswa yang mengajukan</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.siswaMengajukan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, siswaMengajukan: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Kelas</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.kelas || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, kelas: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>NIS</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.nis || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, nis: e.target.value })}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal Mulai Lembur</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalLembur || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, tanggalLembur: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Tanggal Selesai Lembur</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.endOvertimeDate || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, endOvertimeDate: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Mulai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamMulai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamMulai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Selesai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamSelesai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamSelesai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jumlah Jam Lembur</DetailLabel>
                    <input
                      type="number"
                      value={selectedPermit.overtimeHours || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, overtimeHours: e.target.value })}
                      required
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Alasan</DetailLabel>
                    <textarea
                      value={selectedPermit.alasan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, alasan: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Jenis Pekerjaan</DetailLabel>
                    <textarea
                      value={selectedPermit.jenisPekerjaan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jenisPekerjaan: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>

                  <DetailGroup>
                    <DetailLabel>Nama Guru Bengkel</DetailLabel>
                    <TeacherSuggestContainer>
                      <input
                        type="text"
                        value={selectedPermit.guruBengkel || ''}
                        onChange={e => handleTeacherInputChange(e.target.value)}
                        onBlur={handleTeacherInputBlur}
                        onFocus={() => {
                          if (selectedPermit.guruBengkel?.trim() && teacherSuggestions.length > 0) {
                            setShowSuggestions(true);
                          }
                        }}
                        placeholder="Ketik nama guru..."
                        autoComplete="off"
                      />
                      {showSuggestions && teacherSuggestions.length > 0 && (
                        <SuggestionsDropdown>
                          {teacherSuggestions.map((teacher) => (
                            <SuggestionItem
                              key={teacher.id}
                              onClick={() => handleTeacherSelect(teacher)}
                              selected={selectedTeacherId === teacher.id}
                            >
                              <TeacherName>{teacher.name}</TeacherName>
                            </SuggestionItem>
                          ))}
                        </SuggestionsDropdown>
                      )}
                    </TeacherSuggestContainer>
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              {/* Images Section */}
              {selectedPermit.images && selectedPermit.images.length > 0 && (
                <DetailSection>
                  <DetailLabel>Lampiran Gambar</DetailLabel>
                  <ImagesGrid>
                    {selectedPermit.images.map((image, index) => {
                      // Handle different image formats similar to SicknessForm
                      let imgSrc = '';
                      if (typeof image === 'string') {
                        imgSrc = image;
                      } else if (image && image.image_url) {
                        // API returns image objects with image_url property
                        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                        imgSrc = image.image_url.startsWith('http') 
                          ? image.image_url 
                          : `${baseUrl}/${image.image_url}`;
                      }
                      
                      return (
                        <ImageContainer key={index}>
                          <img
                            src={imgSrc}
                            alt={`Lampiran ${index + 1}`}
                            onClick={() => window.open(imgSrc, '_blank')}
                            onError={(e) => {
                              console.error('Image failed to load:', imgSrc);
                              e.target.style.display = 'none';
                            }}
                          />
                        </ImageContainer>
                      );
                    })}
                  </ImagesGrid>
                </DetailSection>
              )}

              <ActionButtons>
              <RejectButton 
                  type="button" 
                  onClick={async () => {
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'ditolak',
                        overtime_date: selectedPermit.tanggalLembur && selectedPermit.tanggalLembur.trim() !== '' ? selectedPermit.tanggalLembur : null,
                        end_overtime_date: selectedPermit.endOvertimeDate && selectedPermit.endOvertimeDate.trim() !== '' ? selectedPermit.endOvertimeDate : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        overtime_hours: selectedPermit.overtimeHours || null,
                        purpose: selectedPermit.alasan || null,
                        type_or_work: selectedPermit.jenisPekerjaan || null,
                        teacher_name: selectedPermit.guruBengkel || null,
                        teacher_id: selectedPermit.teacher_id || null
                      };
                      
                      console.log('Sending reject request data:', requestData);
                      console.log('Selected permit ID:', selectedPermit.id);
                      
                      const res = await authFetch(`${apiBase}/api/admin/overtime-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      console.log('Status updated successfully:', result);
                      
                      setNotification({
                        show: true,
                        message: 'Laporan lembur ditolak dan data diperbarui!',
                        type: 'info'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(searchQuery, dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Tolak
                </RejectButton>
                <ApproveButton 
                  type="button" 
                  onClick={async () => {
                    // Validate required field
                    if (!selectedPermit.overtimeHours || selectedPermit.overtimeHours.trim() === '') {
                      setNotification({
                        show: true,
                        message: 'Jumlah Jam Lembur harus diisi sebelum menyetujui laporan!',
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                      return;
                    }
                    
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'disetujui',
                        overtime_date: selectedPermit.tanggalLembur && selectedPermit.tanggalLembur.trim() !== '' ? selectedPermit.tanggalLembur : null,
                        end_overtime_date: selectedPermit.endOvertimeDate && selectedPermit.endOvertimeDate.trim() !== '' ? selectedPermit.endOvertimeDate : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        overtime_hours: selectedPermit.overtimeHours || null,
                        purpose: selectedPermit.alasan || null,
                        type_or_work: selectedPermit.jenisPekerjaan || null,
                        teacher_name: selectedPermit.guruBengkel || null,
                        teacher_id: selectedPermit.teacher_id || null
                      };
                      
                      console.log('Sending request data:', requestData);
                      console.log('Selected permit ID:', selectedPermit.id);
                      
                      const res = await authFetch(`${apiBase}/api/admin/overtime-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      console.log('Status updated successfully:', result);
                      
                      setNotification({
                        show: true,
                        message: 'Laporan lembur berhasil disetujui dan data diperbarui!',
                        type: 'success'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(searchQuery, dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Disetujui
                </ApproveButton>
                
              </ActionButtons>
            </DetailContent>
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  // max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-dark);
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const TeacherSuggestContainer = styled.div`
  position: relative;
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SuggestionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  transition: background-color 0.2s;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  
  &:hover {
    background-color: var(--background-light);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TeacherName = styled.div`
  font-weight: 500;
  color: var(--text-dark);
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--primary)'};
    color: white;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-light);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminLaporanLemburPage;